import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { generateRecipe, publishRecipe, getChallenges } from '../lib/api';
import { 
  ChevronLeft, ChefHat, Sparkles, Clock, Flame, Dumbbell,
  Check, Share2, Loader2, RefreshCw, Globe, Users, Stethoscope
} from 'lucide-react';

export default function ChefBot() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const tr = (key, fb) => { const v = t(key); return !v || v === key ? fb : v; };
  const navigate = useNavigate();
  
  const [recipe, setRecipe] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [mealType, setMealType] = useState('pranzo');
  const [error, setError] = useState(null);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [visibility, setVisibility] = useState('public');

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setPublished(false);
    setRecipe(null);
    setImageUrl(null);

    try {
      const res = await generateRecipe(mealType, language);
      setRecipe(res.data.recipe);
      setImageUrl(res.data.image_url);
    } catch (err) {
      console.error('Generate recipe error:', err);
      setError(tr('chef.errorGenerate', 'Errore nella generazione della ricetta. Riprova.'));
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!recipe) return;

    // Load challenges if needed
    try {
      const res = await getChallenges();
      setChallenges(res.data.filter(c => new Date(c.end_date) >= new Date()));
    } catch (err) {
      console.error('Error loading challenges:', err);
    }

    setShowVisibilityModal(true);
  };

  const confirmPublish = async () => {
    setPublishing(true);
    setShowVisibilityModal(false);

    try {
      await publishRecipe(recipe, imageUrl, visibility === 'challenge' ? selectedChallenge?.challenge_id : null);
      setPublished(true);
    } catch (err) {
      console.error('Publish recipe error:', err);
      setError(tr('chef.errorPublish', 'Errore nella pubblicazione. Riprova.'));
    } finally {
      setPublishing(false);
    }
  };


  return (
    <div className="min-h-screen pb-20" data-testid="chef-bot-page">
      
      {/* Header */}
      <div className="px-4 pb-4 flex items-center gap-3" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)' }}>
        <button 
          onClick={() => navigate(-1)}
          className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}
        >
          <ChevronLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
        </button>
        <div className="flex-1">
          <h1 className={`font-display text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>Chef AI</h1>
          <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{tr('chef.subtitle', 'Ricette fitness generate con AI')}</p>
        </div>
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isDark ? 'bg-[#E8334A]/20' : 'bg-[#E8334A]/10'}`}>
          <ChefHat className="w-6 h-6 text-[#E8334A]" />
        </div>
      </div>

      {/* Meal Type Selector */}
      <div className="px-4 mb-6">
        <div className={`p-1 rounded-2xl flex ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
          {['colazione', 'pranzo', 'cena', 'snack'].map(type => (
            <button
              key={type}
              onClick={() => setMealType(type)}
              className={`flex-1 py-3 rounded-xl text-sm font-medium capitalize transition-all ${
                mealType === type
                  ? 'bg-[#E8334A] text-white shadow-lg'
                  : isDark ? 'text-white/60' : 'text-gray-600'
              }`}
            >
              {tr(`chef.meal.${type}`, type)}
            </button>
          ))}
        </div>
      </div>

      {/* GLP-1 support banner — informs user that recipes are adapted
          for their therapy. Always includes non-medical disclaimer. */}
      {user?.glp1_support && (
        <div className="px-4 mb-4" data-testid="glp1-disclaimer-banner">
          <div className={`rounded-2xl p-4 border ${isDark ? 'bg-[#1d3557]/20 border-[#1d3557]/40' : 'bg-[#1d3557]/5 border-[#1d3557]/20'}`}>
            <div className="flex items-start gap-3">
              <Stethoscope className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-[#7ba8d9]' : 'text-[#1d3557]'}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('chef.glp1Active') || 'Supporto GLP-1 attivo'}
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  {t('chef.glp1Disclaimer') || 'Le ricette sono adattate per una terapia GLP-1: porzioni moderate, proteiche e facilmente digeribili. Questo non è un consiglio medico: segui sempre le indicazioni del tuo medico o nutrizionista.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate Button */}
      {!recipe && !loading && (
        <div className="px-4 mb-6">
          <button
            onClick={handleGenerate}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#E8334A] to-[#F59E0B] text-white font-semibold flex items-center justify-center gap-2 shadow-lg active:scale-[0.98] transition-transform"
            data-testid="generate-recipe-btn"
          >
            <Sparkles className="w-5 h-5" />
            {tr('chef.generate', 'Genera Ricetta')}
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="px-4">
          <div className="glass-card p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#E8334A]/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
              <ChefHat className="w-8 h-8 text-[#E8334A]" />
            </div>
            <p className={`font-display mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {tr('chef.creating', 'Lo chef sta creando la tua ricetta...')}
            </p>
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-[#E8334A]" />
              <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {tr('chef.generating', 'Generazione in corso')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="px-4 mb-4">
          <div className="glass-card p-4 border border-[#E8334A]/30">
            <p className="text-[#E8334A] text-sm text-center">{error}</p>
          </div>
        </div>
      )}

      {/* Recipe Card */}
      {recipe && !loading && (
        <div className="px-4">
          <div className="glass-card overflow-hidden">
            {/* Image */}
            {imageUrl && (
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={imageUrl} 
                  alt={recipe.nome}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h2 className="font-display text-xl text-white">{recipe.nome}</h2>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="p-4">
              {!imageUrl && (
                <h2 className={`font-display text-xl mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {recipe.nome}
                </h2>
              )}
              
              <p className={`text-sm mb-4 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                {recipe.descrizione}
              </p>

              {/* Macros */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                <div className={`p-3 rounded-xl text-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                  <Clock className="w-4 h-4 mx-auto mb-1 text-[#1d3557]" />
                  <span className={`text-xs block ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{tr('chef.time', 'Tempo')}</span>
                  <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {recipe.tempo_preparazione}
                  </span>
                </div>
                <div className={`p-3 rounded-xl text-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                  <Flame className="w-4 h-4 mx-auto mb-1 text-[#E8334A]" />
                  <span className={`text-xs block ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{tr('chef.calories', 'Calorie')}</span>
                  <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {recipe.calorie}
                  </span>
                </div>
                <div className={`p-3 rounded-xl text-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                  <Dumbbell className="w-4 h-4 mx-auto mb-1 text-[#16A34A]" />
                  <span className={`text-xs block ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{tr('chef.protein', 'Proteine')}</span>
                  <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {recipe.proteine}
                  </span>
                </div>
                <div className={`p-3 rounded-xl text-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                  <span className={`text-sm font-bold block ${isDark ? 'text-[#F59E0B]' : 'text-[#F59E0B]'}`}>C</span>
                  <span className={`text-xs block ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{tr('chef.carbs', 'Carbo')}</span>
                  <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {recipe.carboidrati}
                  </span>
                </div>
              </div>

              {/* Ingredients */}
              <div className="mb-4">
                <h3 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{tr('chef.ingredients', 'Ingredienti')}</h3>
                <ul className={`text-sm space-y-1 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  {recipe.ingredienti?.slice(0, 8).map((ing, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#E8334A]">•</span>
                      {ing}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Procedure */}
              <div className="mb-4">
                <h3 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{tr('chef.procedure', 'Procedimento')}</h3>
                <ol className={`text-sm space-y-2 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  {recipe.procedimento?.slice(0, 5).map((step, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-[#E8334A]/10 text-[#E8334A] text-xs flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <button
                  onClick={handleGenerate}
                  className="btn-secondary flex-1 flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  <RefreshCw className="w-4 h-4" />
                  {tr('chef.newRecipe', 'Nuova')}
                </button>
                <button
                  onClick={handlePublish}
                  disabled={publishing || published}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="publish-recipe-btn"
                >
                  {published ? (
                    <>
                      <Check className="w-4 h-4" />
                      {tr('chef.published', 'Pubblicato')}
                    </>
                  ) : publishing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {tr('chef.publishing', 'Pubblico...')}
                    </>
                  ) : (
                    <>
                      <Share2 className="w-4 h-4" />
                      {tr('chef.publish', 'Pubblica')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visibility Modal */}
      {showVisibilityModal && (
        <div 
          className="fixed inset-0 flex items-end justify-center bg-black/60 backdrop-blur-sm overflow-hidden"
          style={{ zIndex: 9999 }}
          onClick={() => setShowVisibilityModal(false)}
        >
          <div 
            className={`w-full max-w-md rounded-t-3xl p-6 animate-slide-up ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
            style={{ paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}
            onClick={e => e.stopPropagation()}
          >
            <div className={`w-12 h-1 rounded-full mx-auto mb-6 ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
            
            <h2 className={`font-display text-xl mb-6 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {tr('chef.whereToPublish', 'Dove vuoi pubblicare?')}
            </h2>

            <div className="space-y-3 mb-6">
              <button
                onClick={() => { setVisibility('public'); setSelectedChallenge(null); }}
                className={`w-full p-4 rounded-xl flex items-center gap-3 transition-all ${
                  visibility === 'public'
                    ? 'bg-[#1d3557] text-white ring-2 ring-[#1d3557]/50'
                    : isDark ? 'bg-white/5 text-white/80' : 'bg-gray-100 text-gray-700'
                }`}
              >
                <Globe className="w-5 h-5" />
                <div className="flex-1 text-left">
                  <p className="font-medium">{tr('chef.publicLabel', 'Pubblico')}</p>
                  <p className={`text-xs ${visibility === 'public' ? 'text-white/70' : isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    {tr('chef.publicDesc', 'Visibile a tutti nel feed')}
                  </p>
                </div>
                {visibility === 'public' && <Check className="w-5 h-5" />}
              </button>

              {challenges.length > 0 && (
                <>
                  <div className={`text-xs px-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    {tr('chef.orShareChallenge', 'O condividi in una challenge:')}
                  </div>
                  {challenges.map(challenge => (
                    <button
                      key={challenge.challenge_id}
                      onClick={() => { setVisibility('challenge'); setSelectedChallenge(challenge); }}
                      className={`w-full p-4 rounded-xl flex items-center gap-3 transition-all ${
                        visibility === 'challenge' && selectedChallenge?.challenge_id === challenge.challenge_id
                          ? 'bg-[#E8334A] text-white ring-2 ring-[#E8334A]/50'
                          : isDark ? 'bg-white/5 text-white/80' : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      <div className="text-xl">{challenge.emoji}</div>
                      <div className="flex-1 text-left">
                        <p className="font-medium">{challenge.name}</p>
                        <p className={`text-xs flex items-center gap-1 ${
                          visibility === 'challenge' && selectedChallenge?.challenge_id === challenge.challenge_id
                            ? 'text-white/70'
                            : isDark ? 'text-white/50' : 'text-gray-500'
                        }`}>
                          <Users className="w-3 h-3" />
                          {challenge.participants?.length || 1} {tr('chef.participants', 'partecipanti')}
                        </p>
                      </div>
                      {visibility === 'challenge' && selectedChallenge?.challenge_id === challenge.challenge_id && (
                        <Check className="w-5 h-5" />
                      )}
                    </button>
                  ))}
                </>
              )}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowVisibilityModal(false)} className="btn-secondary flex-1">
                {tr('chef.cancel', 'Annulla')}
              </button>
              <button onClick={confirmPublish} className="btn-primary flex-1">
                {tr('chef.publish', 'Pubblica')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
